import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import golfTeam from '../../assets/images/golf-team.svg';
import golfTeamAlert from '../../assets/images/golf-team-alert.svg';
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  LockIcon,
  UsersIcon,
  UserIcon,
} from 'lucide-react';
import { useTeamRegistrationStore } from '../../store/teamRegistrationStore';
import TeamCountDropdown from './components/TeamCountDropdown';
import { useAssociationStore } from '../../store/associationStore';
import { useReportStore } from '../../store/reportStore';
import { TeamRegistrationService } from './TeamRegistrationService';
import TotalPrice from './components/TotalPrice';
import NoParticipationBadge from './components/NoParticipationBadge';
import BWGVLogo from '../../assets/association/BWGV.jpg';
import GVNBLogo from '../../assets/association/GVNB.jpg';
import TaxSummary from './components/TaxSummary';
import LGVHamburgLogo from '../../assets/association/LGV Hamburg.jpg';
import clsx from 'clsx';

const symbolToLogo: Record<string, string> = {
  BWGV: BWGVLogo,
  GVNB: GVNBLogo,
  LGVHAMBURG: LGVHamburgLogo,
};

const getAssociationLogo = (symbol?: string): string | undefined => {
  if (!symbol) return undefined;
  const normalized = symbol.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return symbolToLogo[normalized];
};

interface TeamTabProps {
  onNext?: () => void;
  onPrevious?: () => void;
  showNextButton?: boolean;
  showPreviousButton?: boolean;
}

const TeamTab: React.FC<TeamTabProps> = ({
  onNext,
  onPrevious,
  showNextButton = true,
  showPreviousButton = true,
}) => {
  const report = useReportStore((state) => state.report);
  const teams = useTeamRegistrationStore((state) => state.teams);
  const setTeams = useTeamRegistrationStore((state) => state.setTeams);
  const setSpielgemeinschaftClub = useTeamRegistrationStore(
    (state) => state.setSpielgemeinschaftClub,
  );
  const association = useAssociationStore((state) => state.association);
  const reporterName = useTeamRegistrationStore((state) => state.reporterName);
  const setReporterName = useTeamRegistrationStore(
    (state) => state.setReporterName,
  );
  const editId = useTeamRegistrationStore((state) => state.editId);
  const logoUrl = useMemo(
    () => getAssociationLogo(association?.symbol),
    [association?.symbol],
  );

  // No external dropdown; TeamCountDropdown manages its own open state

  const [selectedCounts, setSelectedCounts] = useState<
    Record<number, number | null>
  >({});
  const handleReportSelection = (index: number, count: number | null) => {
    setSelectedCounts((prev) => ({ ...prev, [index]: count }));
    syncTeamSelection(index, count);
  };

  // Restore persisted selections from store when returning to this tab
  useEffect(() => {
    if (!report?.list) return;
    const fromStore: Record<number, number | null> = {};
    teams.forEach((t) => {
      fromStore[t.id] = t.selectedTeams ?? null;
    });
    if (Object.keys(fromStore).length > 0) {
      setSelectedCounts((prev) => ({ ...prev, ...fromStore }));
    }
  }, [teams, report?.list]);

  const handleNextStep = () => {
    if (onNext) {
      onNext();
    }
  };

  const handlePreviousStep = () => {
    if (onPrevious) {
      onPrevious();
    }
  };

  // No local options generator; TeamCountDropdown renders options

  const {
    net: totalNet,
    vat: totalVat,
    gross: totalPrice,
  } = useMemo(
    () => TeamRegistrationService.computeTotalsFromTeams(teams),
    [teams],
  );

  // Sync selection only when user changes a dropdown, not on tab switches
  const syncTeamSelection = (index: number, count: number | null) => {
    const item = report?.list?.[index];
    if (!item) return;
    const previous = teams.find((t) => t.id === index);
    const filtered = teams.filter((t) => t.id !== index);
    if (count === null) {
      // remove from teams if reset to Bitte auswählen
      setTeams(filtered);
      return;
    }
    // Carry an already typed Spielgemeinschaft club over to the new count,
    // but drop it as soon as no team is reported for this league any more.
    const spielgemeinschaftClub =
      count > 0 ? previous?.spielgemeinschaftClub : undefined;
    setTeams([
      ...filtered,
      {
        ...item,
        id: index,
        selectedTeams: count,
        klasse: index + 1,
        spielgemeinschaftClub,
      },
    ]);
  };

  return (
    <div>
      {/* Association Information */}
      <div className="mb-6 relative cursor-default p-4 bg-gradient-to-b from-gray-50 to-white border-t border-l border-r border-gray-200 border-b-white rounded-t-lg">
        <div className="absolute -top-2.5 px-2 bg-gray-50 rounded-full left-10 border border-gray-200 text-xs text-gray-500 flex items-center gap-2">
          <div className="w-1.5 h-1.5 bg-[#52A445] rounded-full"></div>
          <span className="font-semibold text-black">Verband</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-lg overflow-hidden">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={association?.symbol}
                className="w-full h-full object-cover animate-[fadeInScale_0.4s_ease-out]"
              />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                {association?.symbol || '—'}
              </div>
            )}
          </div>
          <div className="animate-[fadeInSlide_0.4s_ease-out_0.2s_both]">
            <h3 className="font-semibold text-gray-900 text-lg">
              {association?.name}
            </h3>
            <p className="text-gray-600">{association?.email}</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 justify-between rounded-t-lg py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-3">
            <div className="flex items-center gap-3">
              <span className="text-lg">🏌️</span>
              <div>
                <h3 className="font-semibold text-gray-900">
                  Mannschaftsmeldung
                </h3>
                <p className="text-xs text-gray-500">
                  Wählen Sie die Anzahl der zu meldenden Mannschaften
                </p>
              </div>
            </div>
            {editId && <NoParticipationBadge />}
          </div>
          <ul className="animate-[fadeInSlide_0.4s_ease-out_0.2s_both]">
            {(report?.list || []).map((item, index) => {
              const active = !item.lock;
              const selected = selectedCounts[index] ?? null;
              const normalizedGender =
                TeamRegistrationService.parseGenderFromText(item.text);

              const teamRow = teams.find((t) => t.id === index);
              const showSpielgemeinschaftInput =
                TeamRegistrationService.needsSpielgemeinschaftClubInput(
                  association,
                  item,
                  selected,
                );
              const spielgemeinschaftClub =
                teamRow?.spielgemeinschaftClub ?? '';
              const spielgemeinschaftInputId = `spielgemeinschaft-club-${index}`;

              return (
                <li
                  key={`${item.text}-${index}`}
                  className={`transition ${active ? 'bg-white hover:bg-blue-50' : 'bg-gray-50'} ${selected !== null && 'border-l-2 border-l-blue-500'} ${index !== (report?.list?.length || 0) - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span>
                        {active ? (
                          <img
                            src={selected === null ? golfTeamAlert : golfTeam}
                            alt=""
                            className="h-8 w-8"
                          />
                        ) : (
                          <LockIcon className="h-5 w-5 mr-1 text-gray-400" />
                        )}
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <div
                            className={`${active ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
                          >
                            {item.text}
                          </div>
                          <div
                            className={`${active ? 'bg-blue-500/10 text-blue-700' : 'bg-gray-100 text-gray-500'} text-xs font-medium px-1.5 py-0.5 rounded-full`}
                          >
                            {item.price} €/Team
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          Max. {item.max} Mannschaft{item.max > 1 ? 'en' : ''}
                        </div>
                      </div>
                    </div>
                    <div>
                      {active && (
                        <TeamCountDropdown
                          selectedCount={selected}
                          gender={normalizedGender}
                          maxTeams={item.max}
                          onChange={(value) =>
                            handleReportSelection(
                              index,
                              value === 0 ? 0 : value,
                            )
                          }
                        />
                      )}
                      {!active && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border bg-white border-gray-200 text-black">
                          bereits gemeldet
                        </span>
                      )}
                    </div>
                  </div>

                  {showSpielgemeinschaftInput && (
                    <div className="px-4 pb-3 pl-[60px] animate-[fadeInSlide_0.3s_ease-out]">
                      <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
                        <div className="flex items-start gap-2 mb-2">
                          <div className="w-7 h-7 shrink-0 bg-blue-500/10 rounded-md flex items-center justify-center">
                            <UsersIcon className="w-3.5 h-3.5 text-blue-600" />
                          </div>
                          <div>
                            <label
                              htmlFor={spielgemeinschaftInputId}
                              className="block text-xs font-semibold text-gray-900"
                            >
                              Spielgemeinschaft - Clubname
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/10 text-blue-700 align-middle">
                                Pflichtfeld
                              </span>
                            </label>
                            <p className="text-xs text-gray-500">
                              Bitte tragen Sie hier den Golfclub ein, mit dem
                              Sie eine Spielgemeinschaft eingehen werden.
                            </p>
                          </div>
                        </div>
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center">
                            <UsersIcon className="w-4 h-4 text-gray-400" />
                          </div>
                          <Input
                            id={spielgemeinschaftInputId}
                            type="text"
                            value={spielgemeinschaftClub}
                            maxLength={
                              TeamRegistrationService.SPIELGEMEINSCHAFT_CLUB_MAX_LENGTH
                            }
                            onChange={(e) =>
                              setSpielgemeinschaftClub(index, e.target.value)
                            }
                            placeholder="Bitte Golfclub der Spielgemeinschaft eingeben"
                            className={clsx(
                              'w-full pl-9',
                              spielgemeinschaftClub.trim() === '' &&
                                'border-red-500 focus-visible:ring-red-500 focus:border-red-500',
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <TaxSummary
        net={totalNet}
        vat={totalVat}
        gross={totalPrice}
        symbol={association?.symbol || ''}
        shouldShow={association?.symbol === 'LGV Hamburg' && totalVat > 0}
      />

      <div className="mt-6 sticky bottom-0 bg-white py-3">
        <div className="px-2">
          <div className="flex items-center justify-end">
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500">
                Gesamtsumme
              </div>
              <TotalPrice
                amount={totalPrice}
                className="text-lg font-bold text-gray-900"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Meldung durch Field */}
      <div className="mt-8">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <UserIcon className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <label
                htmlFor="meldungDurch"
                className="block text-sm font-semibold text-gray-900"
              >
                Meldung durch (Vor- und Nachname)
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/10 text-blue-700 align-middle">
                  Pflichtfeld
                </span>
              </label>
              <p className="text-xs text-gray-500">
                Bitte Vor- und Nachname des Meldenden angeben.
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center">
              <UserIcon className="w-4 h-4 text-gray-400" />
            </div>
            <Input
              id="meldungDurch"
              type="text"
              value={reporterName}
              onChange={(e) => setReporterName(e.target.value)}
              placeholder="Bitte Vor- und Nachname eingeben"
              className={clsx(
                'w-full pl-9',
                reporterName?.trim?.() === '' &&
                  'border-red-500 focus-visible:ring-red-500 focus:border-red-500',
              )}
            />
          </div>
        </div>
      </div>

      {(showNextButton || showPreviousButton) && (
        <div className="mt-8 flex justify-between">
          {showPreviousButton && (
            <Button
              onClick={handlePreviousStep}
              className="px-6 flex items-center gap-2"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Zurück
            </Button>
          )}
          <div className="flex-1"></div>
          {showNextButton && (
            <Button
              onClick={handleNextStep}
              className="px-6 flex items-center gap-2"
            >
              Weiter
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamTab;
